// Les composants qui vont avec `primitivesCss`.
//
// Ils ne portent aucune logique métier : leur seule raison d'être est qu'il
// n'existe qu'UN bouton, UNE chip et UNE case à cocher dans le produit, quelle
// que soit la surface. Toute l'apparence est dans la feuille partagée ; ces
// composants ne font que poser les bonnes classes et les bons attributs
// d'accessibilité (le second point étant celui qu'on oublie en réécrivant un
// <button> à la main pour la trentième fois).
//
// AUCUNE dépendance plateforme : ce qui varie (traduction, notifications,
// persistance du thème) est injecté par l'app.

import { useState, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Monitor, Moon, Sun } from 'lucide-react'

// --- Boutons -----------------------------------------------------------------

export type ButtonVariant = 'plain' | 'primary' | 'secondary' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  plain: 'btn',
  primary: 'btn-primary',
  secondary: 'btn-secondary',
  danger: 'btn-danger',
}

// `onError` est aussi un gestionnaire d'événement DOM : sans l'exclure, sa
// signature entrerait en conflit avec la nôtre (qui reçoit une Error, pas un
// événement).
export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'onError'> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Rendu à la place du contenu pendant l'attente. Vide = juste le spinner. */
  busyLabel?: string
  /**
   * Peut renvoyer une promesse : le bouton affiche alors son propre état
   * d'attente jusqu'à sa résolution. La moitié des actions de la console
   * appellent l'IA et prennent plusieurs secondes — sans ça, l'utilisateur
   * reclique.
   */
  onClick?: () => void | Promise<void>
  /**
   * Appelé si `onClick` rejette. SANS lui, une action qui échoue ne dit RIEN :
   * le bouton reprend son état normal et l'utilisateur croit que c'est passé.
   * L'app décide comment le signaler (toast, bandeau) — le paquet partagé n'a
   * pas à connaître son système de notification.
   */
  onError?: (error: Error) => void
}

export function Button({
  variant = 'plain',
  size = 'md',
  busyLabel,
  onClick,
  onError,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const [busy, setBusy] = useState(false)
  const classes = [VARIANT_CLASS[variant], size !== 'md' && `btn-${size}`, className]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      {...rest}
      className={classes}
      disabled={busy || disabled}
      // Un bouton qui travaille doit le dire aux lecteurs d'écran, pas
      // seulement afficher une roue qui tourne.
      aria-busy={busy || undefined}
      onClick={
        onClick &&
        (async () => {
          const result = onClick()
          if (!(result instanceof Promise)) return
          setBusy(true)
          try {
            await result
          } catch (e) {
            onError?.(e instanceof Error ? e : new Error(String(e)))
          } finally {
            setBusy(false)
          }
        })
      }
    >
      {busy ? (
        <>
          <span className="spinner" />
          {busyLabel}
        </>
      ) : (
        children
      )}
    </button>
  )
}

// --- Chips -------------------------------------------------------------------

export interface ChipProps {
  /** État sélectionné. Rendu en `aria-pressed`, pas seulement en couleur. */
  on?: boolean
  onClick?: () => void
  title?: string
  children: ReactNode
}

export function Chip({ on, onClick, title, children }: ChipProps) {
  return (
    <button
      type="button"
      className={`chip${on ? ' on' : ''}`}
      aria-pressed={on}
      title={title}
      onClick={onClick}
    >
      {children}
    </button>
  )
}

// --- Case à cocher -----------------------------------------------------------

export interface CheckboxProps {
  checked: boolean
  onChange: (checked: boolean) => void
  /** Obligatoire : une case sans intitulé visible doit au moins être nommée. */
  label: string
  /** Case « tout sélectionner » avec une sélection partielle. */
  indeterminate?: boolean
  onClick?: (e: React.MouseEvent) => void
}

export function Checkbox({ checked, onChange, label, indeterminate, onClick }: CheckboxProps) {
  return (
    <input
      type="checkbox"
      className="checkbox"
      checked={checked}
      aria-label={label}
      // `indeterminate` n'existe qu'en propriété DOM, pas en attribut : sans ce
      // callback de ref, une sélection partielle s'afficherait « tout coché ».
      ref={(el) => { if (el) el.indeterminate = !!indeterminate }}
      onChange={(e) => onChange(e.target.checked)}
      onClick={onClick}
    />
  )
}

// --- Champ de formulaire -----------------------------------------------------

export interface FieldProps {
  label: string
  /** Toujours affiché, sous le champ. Un placeholder n'est pas une étiquette. */
  help?: ReactNode
  /** Affiché À CÔTÉ du champ, jamais regroupé en haut du formulaire. */
  error?: string
  htmlFor?: string
  children: ReactNode
}

export function Field({ label, help, error, htmlFor, children }: FieldProps) {
  return (
    <div className="field">
      <label className="field-label" htmlFor={htmlFor}>{label}</label>
      {children}
      {error ? <div className="field-error">{error}</div> : help ? <div className="field-help">{help}</div> : null}
    </div>
  )
}

// --- Bascule de thème --------------------------------------------------------

export type ThemeChoice = 'auto' | 'light' | 'dark'

const THEMES: { id: ThemeChoice; icon: ReactNode; label: string }[] = [
  { id: 'auto', icon: <Monitor size={13} />, label: 'Thème du système' },
  { id: 'light', icon: <Sun size={13} />, label: 'Thème clair' },
  { id: 'dark', icon: <Moon size={13} />, label: 'Thème sombre' },
]

/**
 * Purement présentationnel : l'app fournit la valeur et la persiste comme elle
 * veut (localStorage côté console, chrome.storage côté extension). Le paquet
 * partagé ne connaît ni l'un ni l'autre.
 */
export function ThemeToggle({
  value,
  onChange,
}: {
  value: ThemeChoice
  onChange: (choice: ThemeChoice) => void
}) {
  return (
    <div className="themetoggle" role="group" aria-label="Thème de l'interface">
      {THEMES.map((t) => (
        <button
          key={t.id}
          type="button"
          aria-pressed={value === t.id}
          aria-label={t.label}
          title={t.label}
          onClick={() => onChange(t.id)}
        >
          {t.icon}
        </button>
      ))}
    </div>
  )
}

/**
 * Traduit le choix en attribut `data-theme` sur la racine du document.
 *
 * « auto » RETIRE l'attribut au lieu d'y écrire une valeur : c'est ce qui
 * laisse `@media (prefers-color-scheme)` reprendre la main. Y écrire
 * `data-theme="auto"` donnerait un sélecteur que personne ne cible, et l'app
 * resterait bloquée en clair sans qu'aucune règle ne paraisse fautive.
 */
export function applyTheme(root: HTMLElement, choice: ThemeChoice): void {
  if (choice === 'auto') root.removeAttribute('data-theme')
  else root.setAttribute('data-theme', choice)
}
